import { lazy, StrictMode, Suspense } from 'react';
import { ClioCeremonyEntryPage, ClioCeremonyViewerPage } from './routes/clio-ceremony';
import { createRoot } from 'react-dom/client';
import { createBrowserRouter, RouterProvider } from 'react-router-dom';

import './globals.css';
import { RootPage } from './routes/root';
import { PlatformLayout } from './routes/platform-layout';
import { PlatformTenantsPage } from './routes/platform-tenants';
import { TenantLayout } from './routes/tenant-layout';
import { TenantOverviewPage } from './routes/tenant-overview';
import { TenantSectionPlaceholder } from './routes/tenant-section-placeholder';
import { TenantCeremoniesPage } from './routes/tenant-ceremonies';
import { TenantCeremonyDetailPage } from './routes/tenant-ceremony-detail';
import { PlatformCallbackPage, PlatformLoginPage } from './routes/platform-login';
import { TenantCallbackPage, TenantLoginPage } from './routes/tenant-login';
import { TenantUsersPage } from './routes/tenant-users';
import { PlatformTenantLayout, PlatformTenantOverviewPage } from './routes/platform-tenant-layout';
import { PlatformTenantMembershipsPage } from './routes/platform-tenant-memberships';
import { PlatformOverviewPage, PlatformSectionPlaceholder } from './routes/platform-overview';
import { PlatformUsersPage } from './routes/platform-users';
import { ClioActionPlaceholderPage, ClioCallbackPage, ClioOnboardingStatusPage, ConnectClioPage, TenantIntegrationsPage } from './routes/tenant-integrations';

const CondensedDemoPage = lazy(() => import('./condensed-demo/page').then((module) => ({ default: module.CondensedDemoPage })));

const DescriptorProcessorPage = lazy(() => import('./routes/descriptor-processor').then(module => ({ default: module.DescriptorProcessorPage })));

const router = createBrowserRouter([
  { path: '/t/:tenantId/condensed-demo', element: <TenantLayout condensed />,
    children: [{ index: true, element: <Suspense fallback={<p className="p-8">Opening workspace…</p>}><CondensedDemoPage /></Suspense> }] },
  { path: '/face-descriptor/', element: <Suspense fallback={null}><DescriptorProcessorPage /></Suspense> },
  { path: '/', element: <RootPage /> },
  { path: '/platform/login', element: <PlatformLoginPage /> },
  { path: '/platform/callback', element: <PlatformCallbackPage /> },
  { path: '/tenant/login', element: <TenantLoginPage /> },
  { path: '/tenant/callback', element: <TenantCallbackPage /> },
  { path: '/integrations/clio/callback', element: <ClioCallbackPage /> },
  { path: '/integrations/clio/connect', element: <ConnectClioPage /> },
  { path: '/integrations/clio/onboarding/:attemptId', element: <ClioOnboardingStatusPage /> },
  { path: '/integrations/clio/actions/ceremony', element: <ClioCeremonyEntryPage /> },
  { path: '/integrations/clio/ceremonies/:tenantId/:remoteObjectId', element: <ClioCeremonyViewerPage /> },
  { path: '/integrations/clio/actions/configuration', element: <ClioActionPlaceholderPage experience="configuration" /> },
  {
    path: '/platform',
    element: <PlatformLayout />,
    children: [
      { index: true, element: <PlatformOverviewPage /> },
      { path: 'tenants', element: <PlatformTenantsPage /> },
      { path: 'users', element: <PlatformUsersPage /> },
      { path: 'usage', element: <PlatformSectionPlaceholder name="Usage & spend" /> },
      { path: 'locations', element: <PlatformSectionPlaceholder name="Location index" /> },
      { path: 'templates', element: <PlatformSectionPlaceholder name="Template library" /> },
      {
        path: 'tenants/:tenantId', element: <PlatformTenantLayout />,
        children: [
          { index: true, element: <PlatformTenantOverviewPage /> },
          { path: 'memberships', element: <PlatformTenantMembershipsPage /> },
        ],
      },
    ],
  },
  {
    path: '/t/:tenantId',
    element: <TenantLayout />,
    children: [
      { index: true, element: <TenantOverviewPage /> },
      { path: 'ceremonies', element: <TenantCeremoniesPage /> },
      {
        path: 'ceremonies/:ceremonyId',
        element: <TenantCeremonyDetailPage />,
      },
      {
        path: 'metrics',
        element: <TenantSectionPlaceholder name="Metrics" />,
      },
      { path: 'users', element: <TenantUsersPage /> },
      {
        path: 'locations',
        element: <TenantSectionPlaceholder name="Locations" />,
      },
      {
        path: 'integrations',
        element: <TenantIntegrationsPage />,
      },
      {
        path: 'templates',
        element: <TenantSectionPlaceholder name="Templates" />,
      },
      {
        path: 'billing',
        element: <TenantSectionPlaceholder name="Billing" />,
      },
      { path: 'audit', element: <TenantSectionPlaceholder name="Audit" /> },
    ],
  },
]);

const container = document.getElementById('root');
if (!container) throw new Error('missing #root element in index.html');

createRoot(container).render(
  <StrictMode>
    <RouterProvider router={router} />
  </StrictMode>,
);
